/**
 * The stimulus (concept, positioning statement, pack copy…) a respondent has to read before
 * answering the questions that react to it.
 *
 * The parser deliberately never emits a stimulus as its own question or info_screen — it stamps
 * the verbatim text onto every question in the section as `section_context`. So this component is
 * the ONLY place a respondent can see it; without it a concept test asks "how appealing is this
 * concept?" with the concept nowhere on the page (backend#525).
 *
 * Every question in a section carries the same text, and the take flow shows one question per
 * screen, so re-rendering it per question keeps the concept in front of the respondent rather
 * than duplicating it.
 */

import React from 'react';

const READ_FIRST_LABEL = 'Read this before answering';
const REGION_LABEL = 'Material shown for this question';

interface SurveySectionStimulusProps {
  /** Verbatim stimulus text (`section_context`). Absent/blank renders nothing. */
  context?: string | null;
  /** Optional short section heading (`section_title`). */
  title?: string | null;
}

const SurveySectionStimulus: React.FC<SurveySectionStimulusProps> = ({ context, title }) => {
  const text = context?.trim();
  if (!text) return null;

  return (
    <section
      aria-label={REGION_LABEL}
      className="mb-6 rounded-lg border border-[#07E5D1]/40 bg-[#E8FCFA] p-4 sm:p-5"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-[#06D4C2] mb-2">
        {title?.trim() || READ_FIRST_LABEL}
      </div>
      <div className="text-[#374151] text-base leading-relaxed whitespace-pre-line break-words">
        {text}
      </div>
    </section>
  );
};

export default SurveySectionStimulus;
